import { defineStore } from 'pinia';
import { useCustomerStore } from './customer';
import GeneralHelper from '@/helpers/GeneralHelper';
import { useGeneralStore } from './general';


export const useThemeStore = defineStore('theme', {
	state: () => ({
		themes: [],
		selected_theme: null,
		initial_loading: true,
		is_near_white_theme: false,
	}),
	getters: {
		getAvailableThemes(state) {
			if (!state.customer) return;
			return state.themes;
		},
		getDefaultThemeId(state) {
			const default_theme = state.themes?.find(theme => theme.is_default === true);
			return default_theme ? default_theme.id : null;
		},
		getSelectedThemeColors(state) {
			if (!state.themes?.length) return {};

			// 1. Try to find the currently selected theme
			const selected_theme = state.themes.find(theme => theme.id === state.selected_theme);
			if (selected_theme) return selected_theme.colors;

			// 2. If not found, fall back to the default theme
			const default_theme = state.themes.find(theme => theme.is_default === true);
			if (default_theme) return default_theme.colors;

			// 3. As a last resort, return an empty object
			return {};
		},
		getThemeTitles(state) {
			return state.themes.map((theme_item) => {
				return {
					id: theme_item.id,
					name: theme_item.name
				};
			});
		},
		getSelectedThemeName(state) {
			const active_theme = state.themes.find(theme => theme.id === state.selected_theme);

			if (active_theme) return active_theme.name;

			const default_theme = state.themes.find(theme => theme.is_default === true);

			return default_theme ? default_theme.name : '';
		}
	},
	actions: {
		setThemes(data) {
			this.themes = data;
			this.findAvailableTheme();
			this.initial_loading = false;
		},
		enableTheme(theme_id) {
			this.selected_theme = theme_id;
			this.applyColorThemeVariables();
			const customerStore = useCustomerStore();

			if (customerStore.isLoggedIn && !this.initial_loading) {
				return global.axios.post(process.env.VUE_APP_API_URL + '/account/theme/update', {theme_id: this.selected_theme}).then((response) => {
					// location.reload();
					return Promise.resolve(response);
				}).catch((error) => {
					console.log(error);
					return Promise.reject(error);
				})
			}
		},
		findAvailableTheme() {
			const customerStore = useCustomerStore();

			const customer_theme_id = customerStore.getCustomer?.theme_id;

			if (customerStore.isLoggedIn && customer_theme_id) {
				this.enableTheme(customer_theme_id);
				return;
			}

			if (!this.selected_theme) this.enableTheme(this.getDefaultThemeId);
		},
		applyColorThemeVariables() {
			const generalStore = useGeneralStore();

			const enabled_theme = this.getSelectedThemeColors;


			const primary = (enabled_theme.primary_color || '#ffffff').toLowerCase();
			const near_white = GeneralHelper.isNearWhite(primary);
			this.is_near_white_theme = near_white;

			document.documentElement.style.setProperty('--dark-gray', enabled_theme.primary_color);
			document.documentElement.style.setProperty('--primary', enabled_theme.secondary_color);
			document.documentElement.style.setProperty('--dark', near_white ? '#e3e7ed' : GeneralHelper.darkenHex(primary, 0.15));
			document.documentElement.style.setProperty('--light-dark', near_white ? '#f3f4f6' : GeneralHelper.lighten(primary, 0.12));
			document.documentElement.style.setProperty('--light-primary', GeneralHelper.shift(enabled_theme.secondary_color, 15, 0, 6));
			document.documentElement.style.setProperty('--lighter-primary', GeneralHelper.shift(enabled_theme.secondary_color, 25, 0, 6));
			document.documentElement.style.setProperty('--link-hover', GeneralHelper.darkenHex(enabled_theme.secondary_color, 0.35));
			document.documentElement.style.setProperty('--green-dark', GeneralHelper.darkenHex(enabled_theme.secondary_color, 0.49));
			document.documentElement.style.setProperty('--secondary', enabled_theme.third_color);
			document.documentElement.style.setProperty('--white', enabled_theme.primary_text_color);
			document.documentElement.style.setProperty('--button-color', enabled_theme.button_color);
			document.documentElement.style.setProperty('--text-color', enabled_theme.header_text_color);
			document.documentElement.style.setProperty('--container-background', enabled_theme.container_color ? enabled_theme.container_color : enabled_theme.primary_text_color);
			document.documentElement.style.setProperty('--text-secondary-color', enabled_theme.secondary_text_color);
			document.documentElement.style.setProperty('--odd-price-up', enabled_theme.odd_price_up_color);
			document.documentElement.style.setProperty('--odd-price-down', enabled_theme.odd_price_down_color);
			document.documentElement.style.setProperty('--banner-text-color', enabled_theme.banner_text_color);
			document.documentElement.style.setProperty('--odd-background-color', enabled_theme.odd_color);
			document.documentElement.style.setProperty('--odd-selected-color', enabled_theme.odd_selected_color);
			document.documentElement.style.setProperty('--odd-color-text', enabled_theme.odd_color_text);
			document.documentElement.style.setProperty('--scoreboard-text-color', enabled_theme.scoreboard_text_color ?? '#ffffff');
			document.documentElement.style.setProperty('--linear-gradient-primary-color', enabled_theme.linear_gradient_primary);
			document.documentElement.style.setProperty('--linear-gradient-secondary-color', enabled_theme.linear_gradient_secondary);
			document.documentElement.style.setProperty('--off-gray', enabled_theme.selector_background_color);
			document.documentElement.style.setProperty('--buttons-text-color', enabled_theme.buttons_text_color);
			document.documentElement.style.setProperty('--button-color-hover', enabled_theme.button_color_hover);
			document.documentElement.style.setProperty('--button-color-text-hover', enabled_theme.button_color_text_hover);
			document.documentElement.style.setProperty('--header-background-color', enabled_theme.header_background_color);
			document.documentElement.style.setProperty('--header-text-color', enabled_theme.header_text_color);
			document.documentElement.style.setProperty('--header-text-color-active', enabled_theme.header_text_color_active);
			document.documentElement.style.setProperty('--active-container', enabled_theme.active_container);
			document.documentElement.style.setProperty('--landing-boxes', enabled_theme.landing_boxes);
			document.documentElement.style.setProperty('--landing-odds', enabled_theme.landing_odds);
			document.documentElement.style.setProperty('--landing-odds-text', enabled_theme.landing_odds_text);
			document.documentElement.style.setProperty('--promotion-box', enabled_theme.promotion_box);
			document.documentElement.style.setProperty('--tab-text-color', enabled_theme.tab_text_color);
			document.documentElement.style.setProperty('--tab-text-color-hover', enabled_theme.tab_text_color_hover);
			document.documentElement.style.setProperty('--landing-odds-mobile', enabled_theme.landing_odds_mobile);
			document.documentElement.style.setProperty('--landing-header-text-color-dark-section', enabled_theme.landing_header_text_color_dark_section);
			document.documentElement.style.setProperty('--landing-header-text-color-light-section', enabled_theme.landing_header_text_color_light_section);
			document.documentElement.style.setProperty('--odd-selected-color-text', enabled_theme.odd_selected_color_text);
			document.documentElement.style.setProperty('--scoreboard-boxes', enabled_theme.scoreboard_boxes);
			document.documentElement.style.setProperty('--landing-boxes-text', enabled_theme.landing_boxes_text);
			document.documentElement.style.setProperty('--market-tab-active-color', enabled_theme.market_tab_active_color || enabled_theme.primary_text_color);
			document.documentElement.style.setProperty('--market-sub-line-color', enabled_theme.market_sub_line_color || enabled_theme.secondary_color);

			generalStore.general.enable_landing_background = enabled_theme.enable_landing_background ?? true;

			// Don't forget to handle the loading state if needed
			this.initial_loading = false;
		},
	},
	persist: {
		storage: localStorage,
		paths: ['selected_theme'],
	},
})